/**
 * Registro de marcos / regulaciones reconocidos por AssessOS Field
 * (ADR-012 — mapeo de controles externos en preguntas).
 *
 * Este catalogo no es enum estricto: la validacion Zod del schema acepta
 * cualquier `framework` que cumpla la regex (MAYUSCULAS:VERSION). Este
 * registro se usa para:
 *
 *   - Mostrar el nombre legible del marco en UI admin y reportes.
 *   - Construir la matriz de cobertura del dashboard.
 *   - Advertir (no bloquear) si una plantilla referencia un marco no
 *     registrado todavia.
 *
 * Para agregar un marco nuevo:
 *   1. Anade la entrada aqui respetando la convencion `<CODIGO>:<VERSION>`.
 *   2. El test garantiza que cada clave matchea la regex del schema.
 *   3. Opcionalmente actualiza `docs/template-schema.md`.
 *
 * Convencion de naming:
 *   - Codigo en MAYUSCULAS con guion bajo, ej. `ISO27001`, `NIST_CSF`,
 *     `OEA_RD`, `INDOTEL_RD`.
 *   - Version separada por `:`, ej. `:2022`, `:v8.1`, `:RES-126-2021`.
 *   - Una entrada sin version se admite (ej. `BPM:GENERAL`).
 */

export const KNOWN_FRAMEWORKS = {
  // Internacionales
  'ISO27001:2022': 'ISO/IEC 27001:2022 SGSI requisitos',
  'ISO27002:2022': 'ISO/IEC 27002:2022 controles de SI',
  'ISO27005:2022': 'ISO/IEC 27005:2022 gestion de riesgo SI',
  'ISO27017:2015': 'ISO/IEC 27017:2015 controles de SI para servicios en la nube',
  'ISO27018:2019': 'ISO/IEC 27018:2019 proteccion de PII en nubes publicas como procesadores',
  'ISO27032:2023': 'ISO/IEC 27032:2023 ciberseguridad',
  'ISO20000-1:2018': 'ISO/IEC 20000-1:2018 sistema de gestion de servicios (SGS)',
  'COBIT:2019': 'ISACA COBIT 2019 — gobierno y gestion de la informacion y tecnologia',
  'ITIL:v4': 'ITIL v4 — Service Value System y 34 practicas (Axelos/PeopleCert)',
  'ISO31000:2018': 'ISO 31000:2018 gestion de riesgo',
  'ISO22301:2019': 'ISO/IEC 22301:2019 continuidad de negocio',
  'ISO9001:2015': 'ISO 9001:2015 calidad',
  'ISO14001:2015': 'ISO 14001:2015 medio ambiente',
  'ISO45001:2018': 'ISO 45001:2018 salud y seguridad ocupacional',
  'ISO22000:2018': 'ISO 22000:2018 inocuidad alimentaria',
  'ISO13485:2016': 'ISO 13485:2016 dispositivos medicos - sistemas de gestion de la calidad',
  'ISO50001:2018': 'ISO 50001:2018 gestion de la energia',
  'ISO28000:2022': 'ISO 28000:2022 seguridad de la cadena de suministro',
  'ISO37001:2025': 'ISO 37001:2025 sistemas de gestion antisoborno (ABMS)',
  'ISO37301:2021': 'ISO 37301:2021 sistemas de gestion de compliance (CMS)',
  'ISO42001:2023': 'ISO/IEC 42001:2023 sistemas de gestion de inteligencia artificial (AIMS)',
  'ISO27701:2019': 'ISO/IEC 27701:2019 extension de SGSI para gestion de privacidad (PIMS)',
  'GDPR:2016-679': 'Reglamento (UE) 2016/679 - Reglamento General de Proteccion de Datos (RGPD)',
  'NIST_CSF:2.0': 'NIST Cybersecurity Framework 2.0',
  'NIST_RMF:800-37r2': 'NIST Risk Management Framework (SP 800-37 Rev. 2, dominio publico US Govt)',
  'NIST_PRIVACY:1.0': 'NIST Privacy Framework v1.0 (dominio publico US Govt)',
  'NIST_AI_RMF:1.0':
    'NIST AI Risk Management Framework 1.0 (AI RMF, enero 2023, dominio publico US Govt) — 4 funciones GOVERN/MAP/MEASURE/MANAGE',
  'NIST_SP:800-207':
    'NIST SP 800-207 Zero Trust Architecture (agosto 2020, dominio publico US Govt)',
  'NIST_SP:800-61r2':
    'NIST SP 800-61 Rev. 2 Computer Security Incident Handling Guide (agosto 2012, dominio publico US Govt)',
  'NIST_SP:800-144':
    'NIST SP 800-144 Guidelines on Security and Privacy in Public Cloud Computing (dic 2011, dominio publico US Govt)',
  'CISA:ZTMM-2.0': 'CISA Zero Trust Maturity Model v2.0 (abril 2023, dominio publico US Govt)',
  'CISA:StopRansomware-2023':
    'CISA #StopRansomware Guide (sep 2023, joint CISA/NSA/FBI/MS-ISAC, dominio publico US Govt)',
  'CSA:CCM-4.0':
    'CSA Cloud Controls Matrix v4.0 (Cloud Security Alliance, 17 dominios, referencia por control ID)',
  'COSO_ERM:2017':
    'COSO Enterprise Risk Management (Integrating with Strategy and Performance, 2017)',
  'CIS:v8.1': 'CIS Controls v8.1',
  'PCI_DSS:v4.0.1': 'PCI DSS v4.0.1 (Payment Card Industry Data Security Standard, junio 2024)',

  // Normas tecnicas — seguridad fisica electronica (Sprint T1)
  'IEC62676:2017': 'IEC 62676 Video surveillance systems for use in security applications',
  'IEC60839:2014': 'IEC 60839 Alarm and electronic security systems',
  'ONVIF:Profile-T': 'ONVIF Profile T (video streaming) y derivados S, G, M, A',
  'NDAA:889-2019':
    'US NDAA 2019 Section 889 — equipo de video restringido (Hikvision, Dahua, Huawei, ZTE, Hytera)',

  // Normas tecnicas — intrusion y alarmas (Sprint T2)
  'EN50131:2006+A3':
    'EN 50131-1:2006+A3:2020 Alarm systems - Intruder and hold-up - System requirements',
  'EN50130:2017': 'EN 50130 Alarm systems - General requirements',
  'EN50136:2012': 'EN 50136 Alarm transmission systems and equipment',

  // Normas tecnicas — proteccion contra incendios (Sprint T2)
  'NFPA:72-2025':
    'NFPA 72:2025 National Fire Alarm and Signaling Code (incluye Cap. 11 ciberseguridad expandido)',
  'NFPA:13-2025': 'NFPA 13:2025 Standard for the Installation of Sprinkler Systems',
  'NFPA:25-2026':
    'NFPA 25:2026 Inspection, Testing and Maintenance of Water-Based Fire Protection Systems',
  'EN54-1:2021': 'EN 54-1:2021 Fire detection and fire alarm systems - Introduction',

  // Republica Dominicana — regulaciones sectoriales
  'JM_RD:2018-SEG-CIBER': 'JM RD Reglamento Seguridad Cibernetica y de la Informacion 2018',
  'JM_RD:2009-RIESGO-OPER':
    'JM RD Reglamento de Riesgo Operacional 2009 (Quinta Resolucion 02-04-2009)',
  'JM_RD:2017-LINEAMIENTOS-GIR':
    'JM RD Lineamientos Gestion Integral de Riesgos 2017 (Tercera Resolucion 16-03-2017)',
  'JM_RD:2015-GOB-CORP':
    'JM RD Reglamento Gobierno Corporativo 2015 (Tercera Resolucion 11-09-2015)',
  'JM_RD:2015-PROT-USUARIO':
    'JM RD Reglamento de Proteccion al Usuario de los Servicios Financieros 2015 (Ley 358-05)',
  'JM_RD:CONC-RIESGOS': 'JM RD Reglamento de Concentracion de Riesgos (Ley 183-02 Art. 47)',
  'JM_RD:CAMBIARIO': 'JM RD Reglamento Cambiario (Banco Central + Junta Monetaria)',
  'INDOTEL_RD:RES-126-2021': 'INDOTEL Res. 126-2021 ciberseguridad ISPs',
  'SIMV_RD:R-CNMV-2020-08-MV': 'SIMV R-CNMV-2020-08-MV ciberseguridad mercado de valores',
  'SIMV_RD:DEC-664-12':
    'Decreto 664-12 Reglamento de Aplicacion Ley de Mercado de Valores (modificado por Dec. 119-16)',
  'SIMV_RD:R-CNMV-2019-21-MV':
    'SIMV R-CNMV-2019-21-MV Reglamento Intermediarios de Valores (mod. R-CNMV-2025-30-MV)',
  'SB_RD:SBR-CC-003-13':
    'SB RD Marco de Supervision Basada en Riesgos (Carta Circular SB CC/003/13)',
  'JAPI_RD:LICENCIA': 'JAPI requisitos tecnicos para licencias',
  'HACIENDA_RD:RES-136-2024': 'Ministerio de Hacienda RD - Resolucion 136-2024',
  'LEY172_RD:2013': 'Ley 172-13 RD - Proteccion Integral de Datos Personales',
  'LEY155_RD:2017': 'Ley 155-17 RD - Contra el Lavado de Activos y Financiamiento del Terrorismo',
  'GAFI:40-REC': 'GAFI/FATF - 40 Recomendaciones (Grupo de Accion Financiera Internacional)',

  // NORTIC (Republica Dominicana — OGTIC)
  'NORTIC:A2-2023': 'NORTIC A2:2023 interoperabilidad',
  'NORTIC:A3-2018': 'NORTIC A3:2018 datos abiertos',
  'NORTIC:A4-2024': 'NORTIC A4:2024 gestion de datos',
  'NORTIC:A7-2025': 'NORTIC A7:2025 administracion de la seguridad de la informacion',
  'NORTIC:A8-2025': 'NORTIC A8:2025 ciberseguridad',
  'NORTIC:A9-2025': 'NORTIC A9:2025 riesgos tecnologicos y continuidad operativa',

  // Marcos aduaneros y zonas francas RD
  'OEA_RD:ZF': 'OEA DGA RD - Empresas de zonas francas',
  'OEA_RD:EXPORT': 'OEA DGA RD - Exportadores',
  'OEA_RD:IMPORT': 'OEA DGA RD - Importadores',
  'OEA_RD:NAVIERO': 'OEA DGA RD - Agentes navieros',
  'OEA_RD:ALMACEN': 'OEA DGA RD - Almacenistas/depositos',
  'OEA_RD:PORTUARIO': 'OEA DGA RD - Operadores portuarios',
  'OEA_RD:CORREO': 'OEA DGA RD - Correo expreso',
  'OEA_RD:ADUANAS': 'OEA DGA RD - Agentes de aduanas',
  'OEA_RD:CONSOLIDADOR': 'OEA DGA RD - Agentes consolidadores',
  'CNZFE_RD:LEY-8-90': 'CNZFE Ley 8-90 + Decreto 522-06',

  // Buenas practicas, normas tecnicas y SST
  'BPM:GENERAL': 'Buenas Practicas de Manufactura',
  'HACCP:CODEX': 'HACCP Codex Alimentarius CAC/RCP 1-1969 Rev. 4-2003',
  'MT_RD:SGSST': 'Ministerio de Trabajo RD - SG-SST',
} as const satisfies Record<string, string>;

export type KnownFrameworkId = keyof typeof KNOWN_FRAMEWORKS;

/**
 * Regex canonica para identificadores de framework. Coincide con la del
 * schema (`FrameworkIdSchema` en `templateSchema.ts`). Se exporta aqui
 * para que el test del catalogo pueda verificar cada entrada y para que
 * herramientas externas (linters, generadores) puedan reusarla.
 *
 *   <CODIGO>            ej. 'BPM'
 *   <CODIGO>:<VERSION>  ej. 'ISO27001:2022', 'NIST_CSF:2.0', 'CIS:v8.1',
 *                          'EN54-1:2021', 'EN50131:2006+A3'
 *
 * Reglas:
 *   - CODIGO: empieza con A-Z, sigue con A-Z 0-9 _ - (longitud 1-79).
 *     El guion en el codigo soporta nombres con sub-numero como
 *     EN54-1 (parte 1 de la familia EN 54).
 *   - VERSION (opcional, separada por `:`): A-Z a-z 0-9 . _ - +.
 *     El `+` soporta notacion de enmienda EN (ej. EN50131:2006+A3).
 *
 * Notas de implementacion:
 *   - V1 (ADR-012): version solo mayuscula. Catalogo listo `CIS:v8.1`;
 *     resolvimos en favor del catalogo agregando minusculas a version
 *     (CHANGELOG v0.7.5).
 *   - V2 (Sprint T2): codigo admite `-` para EN54-1; version admite `+`
 *     para EN50131:2006+A3 (CHANGELOG v0.9.0). Superset retro-compatible
 *     del V1.
 */
export const FRAMEWORK_ID_REGEX = /^[A-Z][A-Z0-9_-]*(:[A-Za-z0-9._+-]+)?$/;

/** True si el framework esta en el catalogo `KNOWN_FRAMEWORKS`. */
export function isKnownFramework(framework: string): framework is KnownFrameworkId {
  return Object.prototype.hasOwnProperty.call(KNOWN_FRAMEWORKS, framework);
}

/**
 * Devuelve el nombre legible del framework. Si no esta registrado,
 * devuelve `null` — el caller decide si advertir o ignorar.
 */
export function getFrameworkName(framework: string): string | null {
  if (isKnownFramework(framework)) {
    return KNOWN_FRAMEWORKS[framework];
  }
  return null;
}
